import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Chip,
  useMediaQuery,
  useTheme,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material'
import { History, ExpandMore, ExpandLess } from '@mui/icons-material'
import { supabase } from '../supabaseClient'
import { Property } from '../types'
import { getAdditionalCostsAtDate } from '@repo/supabase'
import { formatPLN } from '@repo/domain'

interface AdditionalCostsColumnProps {
  record: Property
}

interface AdditionalCost {
  id: string
  amount: number | null
  cost_type: string
  description: string
  is_included_in_price: boolean | null
  is_optional: boolean | null
  property_id: string | null
  valid_from?: string
  is_active?: boolean
}

interface AdditionalCostHistory {
  id: string
  additional_cost_id: string
  amount: number
  created_at: string | null
  valid_from: string
  valid_to: string | null
}

export const AdditionalCostsColumn: React.FC<AdditionalCostsColumnProps> = ({ record }) => {
  const [additionalCosts, setAdditionalCosts] = useState<AdditionalCost[]>([])
  const [historyOpen, setHistoryOpen] = useState(false)
  const [selectedCost, setSelectedCost] = useState<AdditionalCost | null>(null)
  const [costHistory, setCostHistory] = useState<AdditionalCostHistory[]>([])
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const loadAdditionalCosts = async () => {
    try {
      setLoading(true)

      // Get all costs at current date using RPC function
      const costsAtDate = await getAdditionalCostsAtDate(
        supabase,
        [record.id],
        new Date()
      )

      // Get all costs (including inactive) from additional_costs table
      const { data: allCosts, error: allCostsError } = await supabase
        .from('additional_costs')
        .select('*')
        .eq('property_id', record.id)
        .order('valid_from', { ascending: false })

      if (allCostsError) throw allCostsError

      // Mark costs as active or inactive by comparing with costsAtDate
      const activeCostIds = new Set((costsAtDate || []).map((c: AdditionalCost) => c.id))

      const costsWithStatus = (allCosts || []).map(cost => ({
        ...cost,
        is_active: activeCostIds.has(cost.id)
      }))

      // Sort: active first, then by valid_from desc
      costsWithStatus.sort((a, b) => {
        if (a.is_active !== b.is_active) {
          return a.is_active ? -1 : 1
        }
        return new Date(b.valid_from).getTime() - new Date(a.valid_from).getTime()
      })

      setAdditionalCosts(costsWithStatus)
    } catch (error) {
      console.error('Error loading additional costs:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAdditionalCosts()
  }, [record.id])

  const loadCostHistory = async (costId: string) => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('additional_cost_versions')
        .select('*')
        .eq('additional_cost_id', costId)
        .order('valid_from', { ascending: false })

      if (error) throw error
      setCostHistory(data || [])
    } catch (error) {
      console.error('Error loading cost history:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleHistoryClick = (cost: AdditionalCost) => {
    setSelectedCost(cost)
    setHistoryOpen(true)
    loadCostHistory(cost.id)
  }

  // Don't render if no costs
  if (additionalCosts.length === 0) {
    return null
  }

  const activeCosts = additionalCosts.filter(c => c.is_active)
  const inactiveCosts = additionalCosts.filter(c => !c.is_active)

  return (
    <Box sx={{
      p: 1.5,
      backgroundColor: 'grey.50',
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'grey.200',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 'bold' }}>
        Koszty dodatkowe
      </Typography>
      <Box sx={{ flexGrow: 1, overflow: 'hidden', pt: 0.5 }}>
      {/* Active costs - always visible */}
      {activeCosts.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
            Aktywne ({activeCosts.length})
          </Typography>
          <List dense disablePadding>
            {activeCosts.map((cost) => (
              <ListItem
                key={cost.id}
                disablePadding
                secondaryAction={
                  <Tooltip title="Historia cen">
                    <IconButton size="small" onClick={() => handleHistoryClick(cost)}>
                      <History fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {cost.description}
                      </Typography>
                      {cost.is_included_in_price && (
                        <Chip label="w cenie" size="small" color="success" sx={{ height: 16, fontSize: '0.65rem' }} />
                      )}
                      {cost.is_optional && (
                        <Chip label="opcjonalny" size="small" color="info" sx={{ height: 16, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {cost.amount !== null ? formatPLN(cost.amount) : '-'}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Inactive costs - collapsible */}
      {inactiveCosts.length > 0 && (
        <Box>
          <ListItemButton
            onClick={() => setExpanded(!expanded)}
            sx={{ px: 0, py: 0.5 }}
          >
            <ListItemText
              primary={
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Historyczne ({inactiveCosts.length})
                </Typography>
              }
            />
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ListItemButton>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <List dense disablePadding>
              {inactiveCosts.map((cost) => (
                <ListItem
                  key={cost.id}
                  disablePadding
                  sx={{ opacity: 0.6 }}
                  secondaryAction={
                    <Tooltip title="Historia cen">
                      <IconButton size="small" onClick={() => handleHistoryClick(cost)}>
                        <History fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {cost.description}
                        </Typography>
                        <Chip label="historyczny" size="small" color="default" sx={{ height: 16, fontSize: '0.65rem' }} />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {cost.amount !== null ? formatPLN(cost.amount) : '-'}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      )}
      </Box>

      {/* History Dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth={isMobile ? false : "md"}
        fullWidth={!isMobile}
        fullScreen={isMobile}
      >
        <DialogTitle>
          Historia cen - {selectedCost?.description}
          <Typography variant="body2" color="text.secondary">
            {record.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Typography>Ładowanie...</Typography>
          ) : costHistory.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Kwota</TableCell>
                    <TableCell>Od</TableCell>
                    <TableCell>Do</TableCell>
                    <TableCell>Utworzono</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costHistory.map((version) => (
                    <TableRow key={version.id}>
                      <TableCell>{formatPLN(version.amount)}</TableCell>
                      <TableCell>
                        {new Date(version.valid_from).toLocaleDateString('pl-PL')}
                      </TableCell>
                      <TableCell>
                        {version.valid_to
                          ? new Date(version.valid_to).toLocaleDateString('pl-PL')
                          : 'Aktualna'
                        }
                      </TableCell>
                      <TableCell>
                        {version.created_at ? new Date(version.created_at).toLocaleDateString('pl-PL') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">
              Brak historii zmian cen dla tego kosztu.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
